import {
  brandMarkTileClass,
  Button,
  Card,
  CardContent,
  cn,
  Skeleton,
} from '@up2stay/up2promo-ui';
import { Loader2, Plug } from 'lucide-react';

import { useReputationSyncOptional } from '@/core/components/reputation/reputation-sync-context';
import { REPUTATION_SECTION_MUTED_CLASS } from '@/core/components/reputation/reputation-surface';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';

type PlaceholderKind = 'metrics' | 'inbox' | 'analytics';

export function ReputationSyncPlaceholder({ kind }: { kind: PlaceholderKind }) {
  const ctx = useReputationSyncOptional();
  const dict = useDictionary();
  const { navigateShell } = useNavigateShell();
  const syncDict = dict.reputation?.sync;

  if (!ctx || ctx.isLoading) {
    return null;
  }

  if (ctx.isScopeLinked && ctx.isSyncCompleteForScope) {
    return null;
  }

  if (!ctx.isScopeLinked) {
    const unlinked = syncDict?.unlinked;
    const message = ctx.scopeLocationId
      ? unlinked?.location?.[kind]
      : unlinked?.workspace?.[kind];

    if (!message) return null;

    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center gap-3 p-5 py-10 text-center sm:p-6 sm:py-12">
          <span
            className={cn(
              'flex size-12 items-center justify-center rounded-2xl',
              brandMarkTileClass,
            )}
            aria-hidden
          >
            <Plug className="h-5 w-5" />
          </span>
          <p className={cn(REPUTATION_SECTION_MUTED_CLASS, 'max-w-md')}>
            {message}
          </p>
          <Button
            size="sm"
            onClick={() => navigateShell(shellPaths.moreWorkspaceIntegrations)}
          >
            {unlinked?.cta ?? 'Integrations'}
          </Button>
        </CardContent>
      </Card>
    );
  }

  const message = syncDict?.placeholder?.[kind];
  if (!message) return null;

  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center gap-3 p-5 py-10 text-center sm:p-6 sm:py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className={cn(REPUTATION_SECTION_MUTED_CLASS, 'max-w-md')}>
          {message}
        </p>
        {kind === 'metrics' ? (
          <div className="grid w-full max-w-3xl gap-3 pt-2 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton
                key={i}
                className="h-24 w-full rounded-[var(--r-card)]"
              />
            ))}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
